import React from "react";
import { Field } from "formik";
import FormContainer from "forms/components/FormContainer";
import FormFooter from "forms/components/FormFooter";
import FormRow from "forms/components/FormRow";
import {
  CheckboxField,
  DropdownField,
  FileField,
  RenderField,
  RenderTypeahead,
} from "forms/fields";
import PropTypes from "prop-types";

import SubmitButton from "components/SubmitButton";
import { getFallbackLocale } from "utils/locale";
import Translate from "utils/translate";

import AssetStatusField from "./AssetStatusField";

import styles from "./AssetCreateForm.css";
import formStyles from "forms/forms.css";

const { t } = new Translate("AssetCreateModal");

const AssetCreateForm = ({
  handleSubmit,
  isSubmitting,
  portfolios,
  setFieldValue,
  values: { country },
}) => {
  const setTranslatedFieldValue = (field, { id, translations }) =>
    setFieldValue(field, { id, name: translations[getFallbackLocale()] });

  return (
    <form className={formStyles.form} onSubmit={handleSubmit}>
      <div className={styles.wrapper}>
        <div className={styles.left}>
          <Field component={FileField} name="photo" isPicture />
        </div>
        <div className={styles.right}>
          <FormContainer title={t("portfolio")}>
            <Field
              component={DropdownField}
              name="portfolio"
              placeholder={t("placeholders.portfolios")}
              valueExtractor={(p) => p.name}
              values={portfolios}
            />
          </FormContainer>
          <FormContainer title={t("asset.asset")}>
            <FormRow columns={1}>
              <Field component={RenderField} label={t("asset.name")} name="name" />
            </FormRow>
            <FormRow columns={1}>
              <Field component={RenderField} label={t("asset.address")} name="address" />
            </FormRow>
            <FormRow columns={1}>
              <div className={styles.city}>
                <Field component={RenderField} label={t("asset.city")} name="city" />
                <Field component={RenderField} label={t("asset.zip")} name="zip" />
              </div>
            </FormRow>
            <FormRow columns={1}>
              <Field
                component={RenderTypeahead}
                label={t("country")}
                name="country.name"
                onSelect={(c) => setTranslatedFieldValue("country", c)}
                searchIndex="Country"
                searchKey={`translations.${getFallbackLocale()}`}
              />
            </FormRow>
            {country && country.id && (
              <FormRow columns={1}>
                <Field
                  component={RenderTypeahead}
                  label={t("asset.region")}
                  name="region.name"
                  onSelect={(r) => setTranslatedFieldValue("region", r)}
                  searchIndex="Region"
                  searchKey={`translations.${getFallbackLocale()}`}
                  searchParameters={{
                    facets: "country_id",
                    filters: `country_id:${country.id}`,
                  }}
                />
              </FormRow>
            )}
          </FormContainer>
        </div>
      </div>

      <div className={styles.autogenerate}>
        <Field component={CheckboxField} label={t("autoGenerateFlyer")} name="autogenerateFlyer" />
      </div>

      <FormFooter>
        <Field component={AssetStatusField} name="status" />
        <SubmitButton
          isSubmitting={isSubmitting}
          submitLabel={t("add")}
          submittingLabel={t("adding")}
        />
      </FormFooter>
    </form>
  );
};

AssetCreateForm.propTypes = {
  values: PropTypes.shape({
    country: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  setFieldValue: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  portfolios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

export default AssetCreateForm;
